import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {ConferenceType, GameSteps, ModalType} from "data/enums";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {Bindings} from "data/constants/bindings";
import type {
	IPlayoffPicture,
	IPredictionApiProvider,
	IPredictionPayload,
} from "data/providers/api/prediction.api.provider";
import type {IConferencesStore} from "data/stores/conferences/conferences.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {DEFAULT_LIGHT_PREDICTION, GAME_STEPS, NFC_GAME_STEPS} from "data/constants";
import {createPredictionPayload, extractPredictionResponse} from "data/utils/predictions";
import {type IShareStore} from "data/stores/share/share.store";
import {concat} from "lodash";

export interface IDivisionSlot {
	type: GameSteps;
	position: number;
	squadId: number;
}

export interface ILightPredictionNew {
	nfc: IDivisionSlot[];
	afc: IDivisionSlot[];
}

export interface ICustomSteps {
	nfc: ICustomStep;
	afc: ICustomStep;
}

interface ICustomStep {
	seeding: boolean;
	wildCard: boolean;
}

const COMPLETE_CUSTOM_STEPS: ICustomSteps = {
	[ConferenceType.NFC]: {
		seeding: true,
		wildCard: true,
	},
	[ConferenceType.AFC]: {
		seeding: true,
		wildCard: true,
	},
};

const CUSTOM_STEPS: ICustomSteps = {
	[ConferenceType.NFC]: {
		seeding: false,
		wildCard: false,
	},
	[ConferenceType.AFC]: {
		seeding: false,
		wildCard: false,
	},
};

export interface ILightPredictionsStore {
	customSteps: ICustomSteps;

	get lightPrediction(): ILightPredictionNew;

	get nfcPredictions(): IDivisionSlot[];

	get afcPredictions(): IDivisionSlot[];

	get currentStep(): GameSteps;

	get steps(): GameSteps[];

	get currentStepIndex(): number;

	get nextStep(): GameSteps | undefined;

	get isAllPredictionsDone(): boolean;

	get isPredictionsSaved(): boolean;
	get hasSavedPredictions(): boolean;

	get isPredictionsChanged(): boolean;

	get predictionUpdateDate(): string | null;

	get isCardDisabled(): boolean;
	get hasLPPredictions(): boolean;

	getPredictionByType(type: GameSteps): number;

	getConferenceByType(type: GameSteps): ConferenceType;

	setIsPredictionsSaved(value: boolean): void;

	setHasSavedPredictions(value: boolean): void;

	setIsPredictionsChanged(value: boolean): void;

	setCurrentStep(step: GameSteps): void;

	setSquadByStep(squadId: number, step: GameSteps): void;

	setPredictionsByConference(predictions: IDivisionSlot[], conferenceType: ConferenceType): void;

	getPredictions(): Promise<void>;

	savePredictions(predictions?: ILightPredictionNew): Promise<void>;

	savePredictionsFromLocalStorage(): Promise<void>;

	clearPredictionState(): void;

	setIsCardDisabled: (value: boolean) => void;
}

@injectable()
export class LightPredictionsStore implements ILightPredictionsStore {
	@observable private _steps = GAME_STEPS;
	@observable private _predictions: ILightPredictionNew = DEFAULT_LIGHT_PREDICTION;
	@observable private _currentStep: GameSteps = GameSteps.NFC_EAST;
	@observable private _isPredictionsSaved: boolean = false;
	@observable private _isPredictionsChanged: boolean = false;
	@observable private _hasSavedPredictions: boolean = false;
	@observable private _updatedAt: string | null = null;
	@observable public customSteps: ICustomSteps = CUSTOM_STEPS;
	@observable private _isCardDisabled: boolean = false;

	get lightPrediction() {
		return this._predictions;
	}

	get nfcPredictions() {
		return this._predictions.nfc;
	}

	get afcPredictions() {
		return this._predictions.afc;
	}

	get steps() {
		return this._steps;
	}

	get currentStep() {
		return this._currentStep;
	}

	get currentStepIndex() {
		return this._steps.findIndex((stepType) => stepType === this.currentStep);
	}

	get nextStep() {
		return GAME_STEPS[this.currentStepIndex + 1];
	}

	get isAllPredictionsDone() {
		return [...this.afcPredictions, ...this.nfcPredictions].every(({squadId}) => squadId);
	}

	get isPredictionsSaved() {
		return this._isPredictionsSaved;
	}

	get hasSavedPredictions() {
		return this._hasSavedPredictions;
	}

	get isPredictionsChanged() {
		return this._isPredictionsChanged;
	}

	get predictionUpdateDate() {
		return this._updatedAt;
	}

	get isCardDisabled() {
		return this._isCardDisabled;
	}

	get hasLPPredictions() {
		return (
			concat(this._predictions.nfc, this._predictions.afc).filter((el) => el.squadId !== 0)
				.length > 0
		);
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.PredictionApiProvider) private _predictionApi: IPredictionApiProvider,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore,
		@inject(Bindings.ConferencesStore) private _conferencesStore: IConferencesStore,
		@inject(Bindings.ShareStore) private _shareStore: IShareStore
	) {
		makeAutoObservable(this);
	}

	getPredictionByType = (type: GameSteps) => {
		const conference = this.getConferenceByType(type);
		return (
			this._predictions[conference].find((prediction) => prediction.type === type)?.squadId ||
			0
		);
	};

	getConferenceByType = (type: GameSteps) => {
		return NFC_GAME_STEPS.includes(type) ? ConferenceType.NFC : ConferenceType.AFC;
	};

	@action setIsPredictionsSaved(value: boolean) {
		this._isPredictionsSaved = value;
	}

	@action setHasSavedPredictions(value: boolean) {
		this._hasSavedPredictions = value;
	}

	@action setIsPredictionsChanged(value: boolean) {
		this._isPredictionsChanged = value;
	}

	@action setCurrentStep(step: GameSteps) {
		setTimeout(() => {
			this._currentStep = step;
		}, 100);
	}

	// not work with wild card
	@action setSquadByStep(squadId: number, step: GameSteps) {
		const conference = this.getConferenceByType(step);
		this._predictions[conference].forEach((prediction) => {
			if (prediction.type === step) {
				prediction.squadId = squadId;
			}

			return prediction;
		});
	}

	@action clearPredictionState() {
		this._predictions = DEFAULT_LIGHT_PREDICTION;
		this._currentStep = GameSteps.NFC_EAST;
		this._isPredictionsSaved = false;
		this._isPredictionsChanged = false;
		this.customSteps = CUSTOM_STEPS;
	}

	@action setPredictionsByConference(
		predictions: IDivisionSlot[],
		conferenceType: ConferenceType
	) {
		if (conferenceType === ConferenceType.NFC) {
			this._predictions.nfc = predictions;
		} else {
			this._predictions.afc = predictions;
		}
	}

	@action setIsCardDisabled = (value: boolean) => {
		this._isCardDisabled = value;
	};

	private errorMessage = () => {
		return this._modalStore.showModal(ModalType.ERROR, {
			title: this.i18n.t("modals.error.title", "Error"),
			message: this.i18n.t(
				"modals.error.description",
				"Ups, something went wrong, please try again later."
			),
		});
	};

	@action
	private onGetPredictions(playoffPicture: IPlayoffPicture | null) {
		if (playoffPicture) {
			this._predictions = extractPredictionResponse(
				playoffPicture,
				this._conferencesStore.nfcConferenceId,
				this._conferencesStore.afcConferenceId
			);

			this.setIsPredictionsSaved(true);
			this.setHasSavedPredictions(true);
			this.customSteps = COMPLETE_CUSTOM_STEPS;
			this.clearLocalStoragePredictions();
		} else {
			this.clearPredictionState();
		}
	}

	getLocalStoragePredictions() {
		return JSON.parse(
			localStorage.getItem("localPredictions") || "null"
		) as IPredictionPayload | null;
	}

	clearLocalStoragePredictions() {
		localStorage.removeItem("localPredictions");
	}

	@action
	async getPredictions() {
		try {
			const response = await this._predictionApi.get_prediction();
			const {playoffPicture, updatedAt} = response.data.success;

			runInAction(() => {
				this.onGetPredictions(playoffPicture);
				this._updatedAt = updatedAt;
			});
		} catch (err) {
			this.errorMessage();
		}
	}

	@action
	async savePredictions(predictions?: ILightPredictionNew) {
		const payload = createPredictionPayload(
			predictions ? predictions : this._predictions,
			this._conferencesStore.nfcConferenceId,
			this._conferencesStore.afcConferenceId
		);
		try {
			const response = await this._predictionApi.save_prediction(payload);
			const {playoffPicture, updatedAt} = response.data.success;

			runInAction(() => {
				this.onGetPredictions(playoffPicture);
				this._updatedAt = updatedAt;
				this.setIsPredictionsChanged(false);
			});
		} catch (err) {
			this.errorMessage();
		}
	}

	@action
	async savePredictionsFromLocalStorage() {
		const localPredictions = this.getLocalStoragePredictions();

		if (!localPredictions) {
			return;
		}

		try {
			this.clearLocalStoragePredictions();
			const response = await this._predictionApi.save_prediction(localPredictions);
			const {playoffPicture, updatedAt} = response.data.success;

			runInAction(() => {
				this.onGetPredictions(playoffPicture);
				this._updatedAt = updatedAt;
				this.setIsPredictionsChanged(false);

				void this._shareStore.sharePredictionImage().catch(console.error);
				this._modalStore.showModal(ModalType.SHARE_PICKS, {
					message: "",
				});
			});
		} catch (err) {
			this.errorMessage();
		}
	}
}
